import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';

import ProgramPage from '@arte/asg/lib/react/components/Presse/ProgramPagePress';

import Loader from 'Components/Layout/Loader';

import useAPI from 'Hooks/useAPI';
import useMrcAuth from 'Hooks/useMrcAuth';
import useRouter from 'Hooks/useRouter';
import useTranslation from 'Hooks/useTranslation';

import 'scss/components/placeholder-image.scss';
import 'scss/components/program-page.scss';

const Program = () => {

  const history = useNavigate();
  const { programId } = useParams();

  const [ downloadLink, setDownloadLink ] = useState(null);
  const [ program, setProgram ] = useState(null);

  const API           = useAPI();
  const auth          = useMrcAuth();
  const { buildPath } = useRouter();
  const { t }         = useTranslation();

  const { basePath } = appConfig;

  const programImages = Object.values((program || {}).program_images || {});

  const { recommendations } = program || {};

  // Fetch program data from API, redirect to 404 if not found
  useEffect(() => {
    API.fetchWebonly(programId)
      .then(data => {
        setProgram(data);
      });
  }, [ API, programId ]);

  // Only logged in users have access to download links
  useEffect(() => {
    let linkUrl = {};

    if( auth.loginState ) {
      linkUrl = buildPath('download', {
        id: programId,
        type: 'program',
        token: auth.token
      });
    } else {
      linkUrl = auth.loginURL;
    }

    setDownloadLink(linkUrl);
  }, [ auth.loginState, auth.loginURL, auth.token, buildPath, programId ]);


  // No program data yet, show loading screen
  if (!program) {
    return (
      <Loader />
    );
  }

	const { webOnlyAgendaUrl } = window.appConfig;
  const currentUrl = window.location.href;

  const parents = ( program.parents || [] );
  let parent = null;

  if (parents?.length > 0) {
    parent = parents[0];
  }

  /** Specific rules for mediathek:
   * 1- if program belongs to RC, go to rc
   * 2- defaults to always go back to agenda */
	const onReturnClick = () => {
    if (parent !== undefined && parent !== null) {
      // if program is included in a rc, go back to that
      const linkUrl = buildPath('mediathek', {
        id: parent,
      });
      window.location.href = linkUrl;
    }
    else {
      window.location.href = webOnlyAgendaUrl;
    }

	}

  // "Previous" link should take user to the agenda page if no back history
  const breadcrumb = {
    previous: {
      label: t('previous'),
      onClick: onReturnClick,
    },
  };

  const credits = ( program.crew || [] )
    .reduce((acc, { activity, activityCode, name }) => ({
      ...acc,
      [activityCode]: {
        label: activity,
        values: ((acc[activityCode] && acc[activityCode].values) || []).concat([ name ]),
      },
    }), {});

  const programData = {
    credits: Object.keys(credits).map(code => ({
      code,
      label: credits[code].label,
      values: credits[code].values,
    })),
    fullDescription: program.description_full,
    id: program.id,
    player: program.player,
    shortDescription: program.description_short,
    subtitle: program.subtitle,
    title: program.title,
    broadcastProgrammings: null,
    genrePresse: program.genre_name
  };

  if (program.production_countries) {
    programData.credits.push({
      code: 'COUNTRY',
      label: t('country'),
      values: program.production_countries,
    });
  }

  if (program.production_year) {
    programData.credits.push({
      code: 'PRODUCTION_YEAR',
      label: t('year'),
      values: [ program.production_year.toString() ],
    });
  }

  if (program.casting) {
    programData.credits = programData.credits.concat(
      Object.keys(program.casting).map(code => ({
        code,
        label: t('casting'),
        values: program.casting[code],
      })),
    );
  }

  // "Zones" defines the several different areas of the page.
  const page = {
    zones: [
      // Main content for the program page
      {
        code: {
          id: programId,
          name: 'program_content',
        },
        data: [ programData ],
      },

      // Recommended videos in the right sidebar
      {
        code: {
          id: programId,
          name: 'program_recommendations',
        },
        data: [],
      },

      // Bottom section
      {
        code: {
          id: programId,
          name: 'program_categoryMostViewed',
        },
          data: programImages.length
          ? programImages.map(item => ({
            images: {
              landscape: {
                resolutions: item.alternateResolutions.map(image => ({
                  url: image.url,
                  w: image.width,
                  h: image.height,
                })),
              },
            },
            subtitle: item.caption,
            title: item.copyright,
            type: 'teaser',
            url: !auth.loginState
              ? auth.loginURL
              : buildPath('download', {
                id: `${item.programId}/${item.imageId}`,
                type: 'file',
                token: auth.token
              }),
          }))
          : {},
        oldTitle : t('images'),
        title: ( <a href={ downloadLink }>{ t('images') }</a> ),
      },
    ],
  };

  const programMetas = {
    durationLabel: `${Math.floor(program.duration_rounded / 60)} ${t('minutesSmall')}`,
    icons: []
  };
  programMetas.labels = {
    availability: '',
    nextBroadcast: '',
  };

  if ( program.date_start && program.date_end ) {

    const availabilityBeginLong = format( new Date( program.date_start ) , "dd. LLLL yyyy", {
      locale: de } );

    const availabilityEndLong = format( new Date( program.date_end ), "dd. LLLL yyyy", {
      locale: de } );

    programMetas.labels.availability = `${t('broadcastAvailableOnline')} `;
    programMetas.availability = `${t('broadcastAvailableSmallFrom')} ${availabilityBeginLong} ${t('to')} ${availabilityEndLong}`;
  }

  // The playerElement is an image placeholder when the user isn't logged in.
  // This will be shown instead of the internal embed.
  const playerElement = (!auth.token && (
    <img
      alt={ programData.title }
      className="program__placeholder-image"
      src={ program.image_url }
    />
  ));

  console.debug({
    breadcrumb, page, playerElement, program, programData, programMetas, recommendations
  });

  return (
    <ProgramPage
      breadcrumb={ breadcrumb }
      contact={ null }
      page={ page }
      password={ null }
      playerElement={ playerElement || null }
      program={ programData }
      programMetas={ programMetas }
    />
  );
};

export default Program;
